import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit, Eye, Trash2, X, Search } from 'lucide-react';

const TournamentsTab = ({ 
    tournaments,
    filteredTournaments,
    filter,
    setFilter,
    sports,
    tournamentStatuses,
    selectedTournaments,
    onSelectAll,
    onSelectTournament,
    onEdit,
    onDelete,
    onCreate,
    onBulkStatusChange,
    onBulkDelete,
    onClearSelection,
    canCreateTournament
}) => {
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or location..."
                        value={filter.search || ''}
                        onChange={(e) => setFilter({...filter, search: e.target.value})}
                        className="pl-9 bg-[#0d2847] border-white/10"
                        data-testid="tournaments-search-input"
                    />
                </div>
                <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
                    <SelectTrigger className="w-48 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Statuses</SelectItem>
                        {tournamentStatuses.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filter.sport} onValueChange={(v) => setFilter({...filter, sport: v})}>
                    <SelectTrigger className="w-48 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Sports</SelectItem>
                        {sports.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                
                {/* Bulk Actions for Tournaments */}
                {selectedTournaments.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30">
                        <span className="text-sm text-primary font-medium">
                            {selectedTournaments.length} selected
                        </span>
                        <Select onValueChange={(v) => onBulkStatusChange(v)}>
                            <SelectTrigger className="w-32 h-7 bg-transparent border-white/20 text-xs">
                                <SelectValue placeholder="Set Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                {tournamentStatuses.map(s => (
                                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkDelete}
                            className="text-destructive hover:text-destructive hover:bg-destructive/20 h-7 text-xs"
                            data-testid="bulk-delete-tournaments-btn"
                        >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onClearSelection}
                            className="text-muted-foreground hover:text-white h-7 text-xs"
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                )}
                
                <span className="text-sm text-muted-foreground flex-1 text-right">
                    Showing {filteredTournaments.length} of {tournaments.length} tournaments
                </span>
                {canCreateTournament && (
                    <Button
                        onClick={onCreate}
                        className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                        data-testid="create-tournament-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Tournament
                    </Button>
                )}
            </div>

            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left p-4 w-12">
                                <input
                                    type="checkbox"
                                    checked={filteredTournaments.length > 0 && filteredTournaments.every(t => selectedTournaments.includes(t.id))}
                                    onChange={(e) => onSelectAll(e.target.checked)}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                    data-testid="select-all-tournaments-checkbox"
                                />
                            </th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Tournament</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Sport</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Location</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Dates</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Status</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTournaments.map((tournament) => (
                            <tr key={tournament.id} className={`border-t border-white/5 hover:bg-white/5 ${selectedTournaments.includes(tournament.id) ? 'bg-primary/5' : ''}`}>
                                <td className="p-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedTournaments.includes(tournament.id)}
                                        onChange={(e) => onSelectTournament(tournament.id, e.target.checked)}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                        data-testid={`select-tournament-${tournament.id}`}
                                    />
                                </td>
                                <td className="p-4">
                                    <div>
                                        <p className="font-medium">{tournament.name}</p>
                                        <p className="text-xs text-muted-foreground">{tournament.current_participants}/{tournament.max_participants} participants</p>
                                    </div>
                                </td>
                                <td className="p-4 text-sm">{tournament.sport}</td>
                                <td className="p-4 text-sm text-muted-foreground">{tournament.location}</td>
                                <td className="p-4 text-sm text-muted-foreground">{tournament.start_date}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 text-xs uppercase ${
                                        tournament.tournament_status === 'registration_open' ? 'bg-success/20 text-success' :
                                        tournament.tournament_status === 'in_progress' ? 'bg-secondary/20 text-secondary' :
                                        tournament.tournament_status === 'completed' ? 'bg-muted text-muted-foreground' :
                                        'bg-accent/20 text-accent'
                                    }`}>
                                        {tournament.tournament_status?.replace(/_/g, ' ')}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => onEdit(tournament)} className="text-muted-foreground hover:text-white">
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Link to={`/tournaments/${tournament.id}`}>
                                            <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-white">
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </Link>
                                        <Button size="sm" variant="ghost" onClick={() => onDelete(tournament.id)} className="text-destructive hover:text-destructive">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredTournaments.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">No tournaments found</div>
                )}
            </div>
        </div>
    );
};

export default TournamentsTab;
